import {
  getNominationHistory,
  type NominationHistoryEntry,
  type NomineeDetailResponse,
} from '@api/nominees-api';
import { getVotesByNomination, type VoteDetailResponse } from '@api/votes-api';
import Modal from '@components/modal';
import { getAvatarColor, getInitials } from '@utils/avatar-utils';
import { ArrowRight, ExternalLink, MapPin, Trophy } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Tab = 'edition' | 'history';

interface CurrentEditionCategory {
  categoryId: string;
  name: string;
  voteCount: number;
  ranking: number | null;
  isWinner: boolean;
  isCurrent: boolean;
}

interface HistoryEditionGroup {
  editionId: string;
  editionName: string;
  editionYear: number;
  entries: NominationHistoryEntry[];
}

interface NomineeDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  nominee: NomineeDetailResponse | null;
  editionId: string;
}

export default function NomineeDetailModal({
  isOpen,
  onClose,
  nominee,
  editionId,
}: NomineeDetailModalProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('edition');
  const [currentEditionCategories, setCurrentEditionCategories] = useState<
    CurrentEditionCategory[]
  >([]);
  const [history, setHistory] = useState<NominationHistoryEntry[]>([]);
  const [votes, setVotes] = useState<VoteDetailResponse[]>([]);
  const [isLoadingVotes, setIsLoadingVotes] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const historyFetchedForRef = useRef<string | null>(null);

  const fetchHistory = useCallback((): void => {
    if (!nominee || historyFetchedForRef.current === nominee.participant.id) return;

    historyFetchedForRef.current = nominee.participant.id;
    setIsLoadingHistory(true);
    void getNominationHistory(nominee.participant.id)
      .then((entries) => {
        setHistory(entries);

        const currentEditionEntries = entries
          .filter((h) => h.editionId === editionId)
          .map((entry) => ({
            categoryId: entry.categoryId,
            name: entry.categoryName,
            voteCount: entry.voteCount,
            ranking: entry.ranking,
            isWinner: entry.isWinner,
            isCurrent: entry.nominationId === nominee.id,
          }));
        setCurrentEditionCategories(currentEditionEntries);
      })
      .catch(() => {
        setHistory([]);
        setCurrentEditionCategories([]);
      })
      .finally(() => setIsLoadingHistory(false));
  }, [nominee, editionId]);

  useEffect(() => {
    if (!nominee || !editionId) {
      setCurrentEditionCategories([]);
      setVotes([]);
      setHistory([]);
      setActiveTab('edition');
      historyFetchedForRef.current = null;
      return;
    }

    setIsLoadingVotes(true);
    void getVotesByNomination(nominee.id)
      .then((response) => setVotes(response.data))
      .catch(() => setVotes([]))
      .finally(() => setIsLoadingVotes(false));

    historyFetchedForRef.current = null;
    fetchHistory();
  }, [nominee, editionId, fetchHistory]);

  const handleTabChange = useCallback(
    (tab: Tab): void => {
      setActiveTab(tab);
      if (tab === 'history') fetchHistory();
    },
    [fetchHistory],
  );

  const historyGroups = useMemo((): HistoryEditionGroup[] => {
    const otherEditions = history.filter((h) => h.editionId !== editionId);
    const grouped = new Map<string, HistoryEditionGroup>();

    for (const entry of otherEditions) {
      let group = grouped.get(entry.editionId);
      if (!group) {
        group = {
          editionId: entry.editionId,
          editionName: entry.editionName,
          editionYear: entry.editionYear,
          entries: [],
        };
        grouped.set(entry.editionId, group);
      }
      group.entries.push(entry);
    }

    return [...grouped.values()].sort((a, b) => b.editionYear - a.editionYear);
  }, [history, editionId]);

  if (!nominee) return null;

  const { participant } = nominee;
  const fullName = [participant.name, participant.surname].filter(Boolean).join(' ');

  const handleNavigate = (categoryId: string): void => {
    onClose();
    navigate(`/editions/${editionId}/categories/${categoryId}/nominees`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nominee details">
      <div className="nominee-detail">
        {/* Header with participant info */}
        <div className="nominee-detail__header">
          <div
            className="nominee-detail__avatar"
            style={{ backgroundColor: getAvatarColor(fullName) }}
          >
            {getInitials(fullName)}
          </div>
          <div className="nominee-detail__header-info">
            <h3 className="nominee-detail__name">{fullName}</h3>
            <div className="nd-info-bar">
              {participant.city && (
                <span className="nd-info-bar__item">
                  <MapPin size={12} />
                  {participant.city}
                </span>
              )}
              <span className="nd-info-bar__item">DP ID: {participant.dpDoctorId}</span>
              {participant.profileUrl && (
                <a
                  href={participant.profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nd-info-bar__item nd-info-bar__link"
                >
                  <ExternalLink size={12} />
                  Profile
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="nd-tabs">
          <button
            type="button"
            className={`nd-tabs__tab${activeTab === 'edition' ? ' nd-tabs__tab--active' : ''}`}
            onClick={() => handleTabChange('edition')}
          >
            This Edition
          </button>
          <button
            type="button"
            className={`nd-tabs__tab${activeTab === 'history' ? ' nd-tabs__tab--active' : ''}`}
            onClick={() => handleTabChange('history')}
          >
            History
            {historyGroups.length > 0 && (
              <span className="nd-tabs__count">{historyGroups.length}</span>
            )}
          </button>
        </div>

        {/* Tab content */}
        {activeTab === 'edition' && (
          <div className="nd-tab-content">
            {/* Current edition categories */}
            {currentEditionCategories.length > 0 && (
              <div className="nd-cat-list">
                {currentEditionCategories.map((cat) => (
                  <button
                    key={cat.categoryId}
                    type="button"
                    className={`nd-cat-item${cat.isCurrent ? ' nd-cat-item--active' : ''}`}
                    onClick={() => handleNavigate(cat.categoryId)}
                  >
                    <div className="nd-cat-item__info">
                      <span className="nd-cat-item__name">
                        {cat.name}
                        {cat.isCurrent && <span className="nd-cat-item__current">current</span>}
                        {cat.isWinner && <Trophy size={12} className="nd-cat-item__winner" />}
                      </span>
                      <span className="nd-cat-item__meta">
                        {cat.ranking != null && `#${cat.ranking}`}
                      </span>
                    </div>
                    <ArrowRight size={14} className="nd-cat-item__arrow" />
                  </button>
                ))}
              </div>
            )}

            {/* Votes table */}
            <div className="nd-votes">
              <h4 className="nd-section__title">
                Votes received ({isLoadingVotes ? '…' : votes.length})
              </h4>
              {isLoadingVotes ? (
                <p className="nd-section__empty">Loading votes...</p>
              ) : votes.length === 0 ? (
                <p className="nd-section__empty">No votes yet</p>
              ) : (
                <div className="nominee-detail__votes-table">
                  <table className="data-table data-table--compact">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {votes.map((vote) => (
                        <tr key={vote.id}>
                          <td>{vote.user.email}</td>
                          <td className="nominee-detail__vote-date">
                            {new Date(vote.createdAt).toLocaleDateString('en-US', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="nd-tab-content">
            {isLoadingHistory ? (
              <p className="nd-section__empty">Loading history...</p>
            ) : historyGroups.length === 0 ? (
              <p className="nd-section__empty">No past editions found</p>
            ) : (
              <div className="nd-history-table-wrap">
                <table className="data-table data-table--compact">
                  <thead>
                    <tr>
                      <th>Edition</th>
                      <th>Category</th>
                      <th>Votes</th>
                      <th>Rank</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyGroups.flatMap((group) =>
                      group.entries.map((entry, idx) => (
                        <tr key={entry.nominationId}>
                          {idx === 0 ? (
                            <td
                              rowSpan={group.entries.length}
                              className="nd-history-table__edition"
                            >
                              <span className="nd-history-table__edition-name">
                                {group.editionName}
                              </span>
                              <span className="nd-history-table__edition-year">
                                {group.editionYear}
                              </span>
                            </td>
                          ) : null}
                          <td>{entry.categoryName}</td>
                          <td>{entry.voteCount}</td>
                          <td>{entry.ranking ?? '—'}</td>
                          <td>
                            {entry.isWinner && (
                              <Trophy size={13} className="nd-history-table__winner" />
                            )}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
